import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Skeleton,
  Divider,
} from "@mui/material";
import Notification from "../notifications/Notifications";
import { useLocation, useNavigate } from "react-router-dom";
import PendingTaskTile from "../../components/pending-task-tile/PendingTaskTile";
import bmrSubjectApi from "../../bmr/services/bmrSubjectService";
import TaskNotificationTile from "../../bmr/components/taskNotificationTile/TaskNotificationTile";
import { useSelector } from "react-redux";
import { useSocketInstance } from "../../context/SocketContext";
import { hasCRApprovePermission } from "../../utils/utils";

const TabsWithActivity = ({ dashboardData, loading: parentLoading }) => {
  const userData = useSelector((state) => state.loginData);
  const role_id = userData?.role_id;
    const crPermissions = useSelector((state) => state.loginData?.cr_permissions);
  const navigate = useNavigate();
  const location = useLocation();
  const { notifiactioCount } = useSocketInstance();

  const [activeTab, setActiveTab] = useState(0);

  const [pendingTaskData, setPendingTaskData] = useState([]);
  const [pendingCRData, setPendingCRData] = useState([]);
  const [recentActivityData, setRecentActivityCRData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [crLoading, setCrLoading] = useState(false);

  const TABS = useMemo(
    () =>
      [
        { key: "notifications", label: "Notifications", hasDot: notifiactioCount > 0 },
        { key: "tasks", label: "Tasks", hasDot: pendingTaskData.length > 0 },
        (role_id === 1 || hasCRApprovePermission(crPermissions)) && { key: "cr", label: "Change Requests", hasDot: pendingCRData.length > 0 },
      ].filter(Boolean),
    [role_id, pendingTaskData.length, pendingCRData.length, notifiactioCount]
  );

   const refreshAction = async() => {
     const filter = { filter: { status: "0" } };
      const res = await bmrSubjectApi.requestHistory(10, 1, null, null, filter);
      setPendingCRData((res?.data || []).slice(0, 3));
    }

  const fetchPendingTask = async () => {
    if (pendingTaskData.length) return;
    try {
      setLoading(true);
      const filter = { status: 0, search: "", sort_by: null };
      const response = await bmrSubjectApi.tasklist(10, 1, filter);
      setPendingTaskData((response?.data || []).slice(0, 3));
    } catch (error) {
      console.error("Failed to fetch pending tasks", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCR = async () => {
    if (pendingCRData.length) return;
    try {
      setCrLoading(true);
      refreshAction();
    } catch (error) {
      console.error("Failed to fetch Pending CR", error);
    } finally {
      setCrLoading(false);
    }
  };

  // Use recent activity from parent dashboard data
  useEffect(() => {
    if (dashboardData?.recent_activity) {
      setRecentActivityCRData(dashboardData.recent_activity);
    }
  }, [dashboardData]);

  useEffect(() => {
    fetchPendingTask();
    fetchPendingCR();
  }, []);

  const renderActiveTab = () => {
    const activeKey = TABS[activeTab]?.key;

    if (activeKey === "notifications") {
      return <Notification limitNotifications={3} />;
    }

    if (activeKey === "tasks") {
      return (
        <TaskNotificationTile
          task={pendingTaskData}
          isClosed={false}
          loadMoreData={() => {}}
          page={1}
          maxPages={1}
          loading={loading}
          hasMoreData={false}
          hideActions
          showBoxShadow
        />
      );
    }

    if (activeKey === "cr") {
      return (
        <PendingTaskTile
          task={pendingCRData}
           refreshAgain={refreshAction}
          isClosed={false}
          loadMoreData={() => {}}
          page={1}
          maxPages={1}
          loading={crLoading}
          alignment="Pending CR"
          hasMoreData={false}
          showBoxShadow
        />
      );
    }

    return null;
  };

  const handleViewAll = () => {
    const activeKey = TABS[activeTab]?.key;
    // Check portal_access first - if it's 2, always use BMR routes
    if (userData.portal_access === 2) {
      if (activeKey === "notifications") navigate("/bmr/notifications");
      if (activeKey === "cr") navigate("/bmr/change-requests");
      if (activeKey === "tasks") navigate("/bmr/pending-tasks");
      return;
    }

    // Default to main routes (portal_access === 1 or unknown)
    if (activeKey === "notifications") navigate("/notifications");
    if (activeKey === "cr") navigate("/change-requests");
    if (activeKey === "tasks") navigate("/tasks");
  };

const handleRecentActivity = () => {
  if (location.pathname.includes("/bmr")) {
    navigate("/bmr/notifications", { 
      state: { 
        activity: 'recentActivity'
      }
    });
  } else {
    navigate("/notifications", {
       state: { 
        activity: 'recentActivity'
      }
    });
  }
};

  return (
    <Grid container spacing={2} sx={{ mt: 1 }} alignItems="stretch">
      {/* LEFT SECTION */}
      <Grid item xs={12} md={8}>
        <Paper
          sx={{ p: 2, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
        >
          {/* Tabs */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "6px" }}>
            {TABS.map((tab, index) => {
              const isActive = activeTab === index;

              return (
                <Box
                  key={tab.key}
                  onClick={() => setActiveTab(index)}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    px: 3,
                    py: 1,
                    borderRadius: "999px",
                    fontSize: "14px",
                    fontWeight: 500,
                    backgroundColor: isActive ? "#2563EB" : "#FFFFFF",
                    color: isActive ? "#FFFFFF" : "#6B7280",
                    border: isActive ? "none" : "1px solid #E5E7EB",
                  }}
                >
                  {tab.label}
                  {tab.hasDot && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#EF4444",
                      }}
                    />
                  )}
                </Box>
              );
            })}

            <Box sx={{ flexGrow: 1 }} />

            <Typography
              onClick={handleViewAll}
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#2563EB",
                cursor: "pointer",
              }}
            >
              View All
            </Typography>
          </Box>
                  <Divider sx={{mt:2 }} />

          <Box sx={{ mt: 3 }}>
            {renderActiveTab()}
          </Box>
        </Paper>
      </Grid>

      {/* RIGHT SECTION */}
      <Grid item xs={12} md={4}>
        <Paper
          sx={{ p: 2, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography fontWeight={600}>
              Recent Activity
            </Typography>

            <Typography
              onClick={handleRecentActivity}
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#2563EB",
                cursor: "pointer",
              }}
            >
              View All
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {parentLoading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Skeleton variant="text" width="80%" height={22} />
                    <Skeleton variant="text" width="60%" height={16} />
                  </Box>
                ))
              : recentActivityData.slice(0, 3).map((item, index) => {
                  const isPositive = index % 2 === 0;
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        borderRadius: "16px",
                        backgroundColor: isPositive ? "#E9FBF4" : "#FDECEC",
                        border: "1px solid",
                        borderColor: isPositive ? "#C7F0E2" : "#F8CFCF",
                      }}
                    >
                      <Box>
                        <Typography fontSize="13px" fontWeight={500}>
                          {item.activity}
                        </Typography>
                        <Typography fontSize="13px" color="#6B7280">
                          {item.activity_date}
                          <span style={{ margin: "0 6px" }}>|</span>
                          {item.time}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}

            {!parentLoading && recentActivityData.length === 0 && (
              <Typography fontSize="13px" color="text.secondary">
                No recent activity
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TabsWithActivity;

